import type {
  Campaign,
  ICampaignVariantVersionDestination,
  ICampaignVariantVersionProduct,
  Variant,
} from '~/modules/campaign/types';
import type { IPickPageItem } from '~/modules/modal-pick-page';
import type { PlatformProductStatus } from '~/types/graphql';

import { IDisabledEditingKey } from '~/modules/campaign-group-template/constants';
import { toPickPageTypeFromUrl, IPickPageType } from '~/modules/modal-pick-page';
import { getDisabledEditing } from '~/modules/campaign-group-template/helpers';
import { PICK_PAGE_TYPE_BY_VARIANT_URL_RESOURCE_TYPE } from '../constants';
import { hasVariantUrlDestination } from '~/modules/campaign/helpers';
import { CAMPAIGN_STATUS } from '~/constants/campaign';
import { getShopifyConfig } from '~/config';

export type IVariantUrlRedirectData = NonNullable<NonNullable<Campaign['variants']>[number]>['url'];

/** What the pick modal already knows about a page the moment it is chosen. */
export interface IVariantPageInfo {
  title: string;
  handle: string;
  image: string;
  status?: PlatformProductStatus;
}

export const toPickPageItemFromVariantUrlRedirect = (
  destination: NonNullable<IVariantUrlRedirectData>,
): IPickPageItem => ({
  id: destination.id,
  baseID: destination.baseID,
  handle: destination.handle,
  title: destination.title ?? '',
  url: destination.redirectURL,
  type: PICK_PAGE_TYPE_BY_VARIANT_URL_RESOURCE_TYPE[destination.resourceType],
});

/**
 * A version snapshot predates the destination record and stores the page as a product, so the page
 * type has to be read back off the URL it kept.
 */
export const toPickPageItemFromVersionProduct = (
  product: ICampaignVariantVersionProduct,
  id: string,
): IPickPageItem => ({
  id,
  baseID: product.baseID,
  handle: product.handle,
  title: product.title ?? '',
  url: product.redirectURL ?? '',
  type: toPickPageTypeFromUrl(product.redirectURL),
  status: product.status,
});

/**
 * A snapshot says a page was picked on the same terms the live record does, but it is a plain
 * settings object rather than a destination, so it answers for itself.
 */
const hasVersionDestination = (
  destination?: ICampaignVariantVersionDestination,
): destination is ICampaignVariantVersionDestination =>
  !!destination?.baseID || !!destination?.redirectURL;

/**
 * The snapshot a version keeps of the destination record. `resourceType` is what the pick modal read
 * the page back as, and the homepage — which has neither baseID nor handle — is only ever its URL.
 */
export const toPickPageItemFromVersionDestination = (
  destination: ICampaignVariantVersionDestination,
  id: string,
): IPickPageItem => ({
  id,
  baseID: destination.baseID ?? '',
  handle: destination.handle ?? '',
  title: destination.title ?? '',
  url: destination.redirectURL ?? '',
  type: destination.resourceType
    ? PICK_PAGE_TYPE_BY_VARIANT_URL_RESOURCE_TYPE[destination.resourceType]
    : toPickPageTypeFromUrl(destination.redirectURL),
});

/**
 * The page a variant currently shows, from whichever record holds it — a version being viewed
 * snapshots its own, and it wins over what the variant holds today. Versions cut before the
 * destination record existed snapshot the page as a product instead.
 */
export const toVariantUrlRedirectPage = ({
  variant,
  versionDestination,
  versionProduct,
}: {
  variant: Variant;
  versionDestination?: ICampaignVariantVersionDestination;
  versionProduct?: ICampaignVariantVersionProduct;
}): IPickPageItem | undefined => {
  if (hasVersionDestination(versionDestination))
    return toPickPageItemFromVersionDestination(versionDestination, variant.url?.id ?? variant.id);
  if (versionProduct?.baseID)
    return toPickPageItemFromVersionProduct(versionProduct, variant.url?.id ?? variant.id);
  if (!variant.url || !hasVariantUrlDestination(variant.url)) return undefined;

  return toPickPageItemFromVariantUrlRedirect(variant.url);
};

/**
 * A page keeps the title and artwork it had when it was picked, which a rename in the Shopify admin
 * leaves stale, so what the record says today wins wherever the lookup reached it.
 */
export const withVariantUrlRedirectLivePage = (
  page: IPickPageItem,
  livePage?: IPickPageItem,
): IPickPageItem => {
  if (!livePage) return page;

  return { ...page, title: livePage.title || page.title, image: livePage.image || page.image };
};

/** Overlays what the pick modal knew onto a stored pick, which only kept the values at pick time. */
export const withVariantUrlRedirectPageInfo = (
  page: IPickPageItem,
  pageInfo?: IVariantPageInfo,
): IPickPageItem => {
  if (!pageInfo) return page;

  return {
    ...page,
    handle: pageInfo.handle || page.handle,
    title: pageInfo.title || page.title,
    image: pageInfo.image || page.image,
    status: pageInfo.status ?? page.status,
  };
};

export { hasVariantUrlDestination };

/**
 * Whether a stored destination and a freshly resolved page are the same storefront page. The record
 * id decides wherever both carry one; the homepage has none, so there the URL is the only identity.
 */
const isSameVariantUrlDestination = ({
  destination,
  page,
}: {
  destination: IVariantUrlRedirectData;
  page: IPickPageItem;
}): boolean => {
  if (!destination || !hasVariantUrlDestination(destination)) return false;
  if (destination.baseID && page.baseID) return destination.baseID === page.baseID;

  return !!destination.redirectURL && destination.redirectURL === toVariantUrlRedirectURL(page.url);
};

export interface IFindVariantUrlRedirectConflictParams {
  campaign: Campaign;
  page: IPickPageItem;
  /** The side the URL is being confirmed for, which is not a conflict with itself. */
  variantId: string;
}

/** The other side of this experiment already sending traffic to the same page, if any. */
export const findVariantUrlRedirectConflict = ({
  campaign,
  page,
  variantId,
}: IFindVariantUrlRedirectConflictParams): Variant | undefined =>
  campaign.variants?.find(
    (variant) =>
      !!variant &&
      variant.id !== variantId &&
      isSameVariantUrlDestination({ destination: variant.url, page }),
  ) ?? undefined;

/**
 * Page type the pick modal opens on, so changing a page starts from the tab the current one came from.
 * Undefined while the destination is still the empty one a draft starts on, whose `resourceType`
 * is a placeholder rather than a page anyone picked.
 */
export const toVariantUrlRedirectPickType = (
  destination?: IVariantUrlRedirectData,
): IPickPageType | undefined => {
  if (!destination || !hasVariantUrlDestination(destination)) return undefined;

  return PICK_PAGE_TYPE_BY_VARIANT_URL_RESOURCE_TYPE[destination.resourceType];
};

export interface IVariantUrlRedirectBaseIDGroups {
  productBaseIDs: string[];
  pageBaseIDs: string[];
  collectionBaseIDs: string[];
  articleBaseIDs: string[];
}

/**
 * Each pick type has its own live-lookup API, so a live title refresh has to ask the right one —
 * HOME has no backing record and is left out of every group.
 */
export const groupBaseIDsByPickPageType = (
  items: Array<{ baseID?: string | null; type?: IPickPageType }>,
): IVariantUrlRedirectBaseIDGroups => {
  const groups: IVariantUrlRedirectBaseIDGroups = {
    productBaseIDs: [],
    pageBaseIDs: [],
    collectionBaseIDs: [],
    articleBaseIDs: [],
  };

  // The same baseID can show up on multiple variants (e.g. two experiments redirecting to the
  // same collection); the group list count stays small enough that an includes() check is fine.
  items.forEach(({ baseID, type }) => {
    if (!baseID) return;
    if (type === IPickPageType.PRODUCT && !groups.productBaseIDs.includes(baseID))
      groups.productBaseIDs.push(baseID);
    if (type === IPickPageType.LANDING && !groups.pageBaseIDs.includes(baseID))
      groups.pageBaseIDs.push(baseID);
    if (type === IPickPageType.COLLECTION && !groups.collectionBaseIDs.includes(baseID))
      groups.collectionBaseIDs.push(baseID);
    if (type === IPickPageType.BLOG_POST && !groups.articleBaseIDs.includes(baseID))
      groups.articleBaseIDs.push(baseID);
  });

  return groups;
};

/** Turns a picked storefront path into the absolute URL the destination record stores. */
export const toVariantUrlRedirectURL = (path?: string): string => {
  const shopifyDomain = getShopifyConfig()?.shopifyDomain;
  if (!path || !shopifyDomain) return '';

  return `https://${shopifyDomain}${path}`;
};

export const getVariantUrlRedirectLiveURL = (
  destination?: IVariantUrlRedirectData,
): string | undefined => destination?.redirectURL || undefined;

// A pick is stored as a GID for a product and as the plain id for the other types; the platform's
// `id:` term takes the numeric one either way.
const toNumericPlatformId = (baseID: string): string => baseID.split('/').pop() ?? baseID;

/** Reads a set of picked records back in one listing, whatever page type they are. */
export const toPlatformIdsQuery = (baseIDs: string[]): string =>
  `(${baseIDs.map((baseID) => `id:${toNumericPlatformId(baseID)}`).join(' OR ')})`;

export const computeUrlRedirectChangePageDisabledReason = ({
  campaign,
  isDisabledEditing,
}: {
  campaign: Campaign;
  isDisabledEditing: boolean;
}): string | undefined => {
  if (!isDisabledEditing) return undefined;

  return getDisabledEditing(campaign?.status, IDisabledEditingKey.CHANGE_TEMPLATE);
};

export interface IVariantUrlRedirectActionState {
  canRemovePage: boolean;
  changeDisabledReason?: string;
  removeDisabledReason?: string;
}

export const computeUrlRedirectActionState = ({
  campaign,
  item,
  isDisabledEditing,
}: {
  campaign: Campaign;
  item: Variant;
  isDisabledEditing: boolean;
}): IVariantUrlRedirectActionState => {
  const isCampaignActive = campaign.status === CAMPAIGN_STATUS.RUNNING;

  return {
    // Control anchors the experiment, and a running one can't lose a side mid-flight.
    canRemovePage: !item.isOrigin && !isCampaignActive,
    changeDisabledReason: computeUrlRedirectChangePageDisabledReason({
      campaign,
      isDisabledEditing,
    }),
    removeDisabledReason: isDisabledEditing
      ? getDisabledEditing(campaign?.status, IDisabledEditingKey.REMOVE_TEMPLATE)
      : undefined,
  };
};
